import { toast } from "react-toastify";

/**
 * Upload size limits, enforced in the UI.
 *
 * Checked where the file is picked — in the feed composer and in chat — so an
 * oversized file is refused the moment it's chosen, before anything is uploaded.
 * A member on a slow connection otherwise sat through a long upload of a 200 MB
 * video only to be told no at the end.
 *
 * These are the numbers the product asks for; the media service has its own
 * limits and is the real authority, so this is a courtesy check, not a security
 * boundary.
 */
export const MEDIA_LIMITS = {
  image: 5 * 1024 * 1024,
  video: 50 * 1024 * 1024,
  audio: 15 * 1024 * 1024,
  // Documents go to the media service like everything else; product caps them
  // at 10 MB.
  file: 10 * 1024 * 1024,
};

const KIND_LABEL = { image: "Image", video: "Video", audio: "Audio", file: "File" };

/** 'image' | 'video' | 'audio' | 'file', from the browser's own MIME type. */
export function mediaKind(file) {
  const type = file?.type || "";
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";
  return "file";
}

/** "82.4 MB" — one decimal from MB up, whole numbers below. */
export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
}

const ICON = {
  image: "M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zm0 11l4.5-4.5 3 3L15 11l5 5M9 9.5a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z",
  video: "M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z",
  audio: "M19 11a7 7 0 01-14 0M12 18v3m0-3a4 4 0 01-4-4V7a4 4 0 018 0v7a4 4 0 01-4 4z",
  file: "M14 3v5h5M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z",
};

/**
 * The "too large" toast. Rendered as its own card rather than a one-line string
 * so it answers the two questions a bare "file too large" leaves open: which
 * file, and how far over is it.
 */
function TooLargeToast({ name, kind, size, limit }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 ring-1 ring-amber-100 dark:ring-amber-500/30">
        <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d={ICON[kind] || ICON.file} />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-bold leading-tight text-gray-900">
          {KIND_LABEL[kind] || "File"} is too large
        </p>
        {name && (
          <p className="mt-0.5 truncate text-[12px] font-semibold text-gray-500" title={name}>
            {name}
          </p>
        )}
        <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] font-semibold">
          <span className="text-rose-600 dark:text-rose-300">{formatBytes(size)}</span>
          <span className="text-gray-300 dark:text-gray-500">·</span>
          <span className="text-gray-500">limit {formatBytes(limit)}</span>
        </p>
      </div>
    </div>
  );
}

/** Toast that one file was refused for its size. */
export function toastTooLarge(file, kind = mediaKind(file)) {
  const limit = MEDIA_LIMITS[kind] ?? MEDIA_LIMITS.file;
  toast.warn(
    <TooLargeToast name={file?.name} kind={kind} size={file?.size || 0} limit={limit} />,
    // The card carries its own kind-specific icon chip, so the container's stock
    // warning triangle would just be a second icon next to it.
    { autoClose: 5000, icon: false }
  );
}

/**
 * True when the file is within its kind's limit. When it isn't, this toasts and
 * returns false, so callers read as `if (!withinMediaLimit(file)) return;`.
 */
export function withinMediaLimit(file, kind = mediaKind(file)) {
  const limit = MEDIA_LIMITS[kind] ?? MEDIA_LIMITS.file;
  if (!file || (file.size || 0) <= limit) return true;
  toastTooLarge(file, kind);
  return false;
}

/**
 * Narrow a picked FileList to the images that may actually be added: images
 * only, each within the size limit, and no more than the slots left.
 *
 * Call this BEFORE setState, never inside the updater. React double-invokes
 * state updater functions in development, so a toast raised inside one is shown
 * twice — which is exactly what these pickers used to do.
 *
 * @returns {File[]} the accepted files, for the caller to shape as it likes
 */
export function acceptImages(files, currentCount, max) {
  const picked = Array.from(files || []).filter((f) => f?.type?.startsWith("image/"));
  const sized = picked.filter((f) => withinMediaLimit(f, "image"));
  const room = Math.max(0, max - currentCount);
  if (sized.length > room) toast.warn(`You can add up to ${max} images.`);
  return sized.slice(0, room);
}

/**
 * The files carried by a paste or a drop, from either `clipboardData` or
 * `dataTransfer`. Some browsers leave `.files` empty on a paste and only expose
 * the image through `.items`, so fall back to those.
 */
export function transferFiles(dt) {
  if (!dt) return [];
  const files = Array.from(dt.files || []);
  if (files.length) return files;
  return Array.from(dt.items || [])
    .filter((it) => it.kind === "file")
    .map((it) => it.getAsFile())
    .filter(Boolean);
}

/** True while a drag carries files (not a text selection being moved). */
export function dragHasFiles(e) {
  return Array.from(e.dataTransfer?.types || []).includes("Files");
}
